import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Icon } from '../components/icons';
import { Badge, EmptyState, ErrorState, KeyHint, LoadingRows, ToneDot } from '../components/ui';
import { useToast } from '../context/ToastContext';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errors';
import { formatBytes, formatDayLabel, formatPhone, formatTime, statusLabel } from '../lib/format';
import { useHotkeys } from '../lib/hotkeys';
import { mediaFor, rememberMedia } from '../lib/mediaCache';
import { qk, useConversation } from '../lib/queries';
import type { ConversationDetail, Message } from '../lib/types';

const QUICK_REPLIES = [
  'Merhaba, hemen kontrol ediyorum.',
  'Kargo takip numaranız: ',
  'İade kodunuzu oluşturdum: '
];

const MAX_LEN = 1024;

/** S4 — Konuşma: balonlar, medya, durum, iç not, hızlı yanıt, Ctrl+Enter. */
export default function ConversationPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const detail = useConversation(id);
  const [draft, setDraft] = useState('');
  const [isNote, setIsNote] = useState(false);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const conversation = detail.data?.conversation;
  const messages = detail.data?.messages ?? [];
  const contact = detail.data?.contact;

  // Okundu işareti: yalnız bir kez, konuşma açıldığında.
  useEffect(() => {
    if (!id) return;
    void api.markRead(id).then(() => {
      void queryClient.invalidateQueries({ queryKey: ['inbox'] });
    }).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Yeni mesajda en alta kaydır.
  useEffect(() => {
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages.length]);

  const sendMutation = useMutation({
    mutationFn: (payload: { body: string; note: boolean; media?: { id?: string; url?: string; mime?: string; name?: string } | null }) =>
      payload.note ? api.addNote(id, payload.body) : api.sendMessage(id, payload.body, payload.media ?? null),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: qk.conversation(id) });
      const previous = queryClient.getQueryData<ConversationDetail>(qk.conversation(id));
      if (previous) {
        const optimistic: Message = {
          id: 'gecici-' + Date.now(),
          conversationId: id,
          author: 'agent',
          kind: payload.media ? (String(payload.media.mime || '').startsWith('image/') ? 'image' : 'document') : 'text',
          body: payload.body,
          at: new Date().toISOString(),
          status: 'queued',
          isNote: payload.note,
          mediaName: payload.media?.name,
          mediaUrl: payload.media?.url,
          mediaMime: payload.media?.mime
        };
        queryClient.setQueryData<ConversationDetail>(qk.conversation(id), {
          ...previous,
          messages: [...previous.messages, optimistic]
        });
      }
      return { previous };
    },
    onError: (error, _payload, context) => {
      if (context?.previous) queryClient.setQueryData(qk.conversation(id), context.previous);
      toast.push(errorMessage(error), 'error');
    },
    onSuccess: (message, payload) => {
      if (payload.media) rememberMedia(message.id, { ...payload.media, id: payload.media.id ?? '', url: payload.media.url ?? '', mime: payload.media.mime ?? '', size: 0, name: payload.media.name ?? 'medya' });
      if (message.status === 'blocked') {
        toast.push('Guard engelledi: ' + (message.blockedReason || 'TEST_MODE_BLOCKED') + ' — mesaj gönderilmedi.', 'error');
      } else if (message.status === 'failed') {
        toast.push('Gönderim başarısız: ' + (message.blockedReason || 'WA_SEND_FAILED'), 'error');
      } else {
        toast.push(message.isNote ? 'İç not eklendi.' : 'Mesaj gönderildi.', 'ok');
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: qk.conversation(id) });
      void queryClient.invalidateQueries({ queryKey: ['inbox'] });
    }
  });

  const aiMutation = useMutation({
    mutationFn: (action: 'takeover' | 'resume') => (action === 'takeover' ? api.aiTakeover(id) : api.aiResume(id)),
    onSuccess: (_, action) => {
      toast.push(action === 'takeover' ? 'Konuşmayı devraldınız; AI susacak.' : 'AI tekrar devrede.', 'ok');
      void queryClient.invalidateQueries({ queryKey: qk.conversation(id) });
      void queryClient.invalidateQueries({ queryKey: ['inbox'] });
    },
    onError: (error) => toast.push(errorMessage(error), 'error')
  });

  function submit() {
    const body = draft.trim();
    if (!body || sendMutation.isPending) return;
    sendMutation.mutate({ body, note: isNote });
    setDraft('');
  }

  useHotkeys([
    { key: 'Enter', ctrl: true, handler: () => submit(), allowInInput: true },
    { key: 'Escape', handler: () => navigate('/inbox') },
    { key: 'r', handler: () => composerRef.current?.focus() }
  ]);

  const lastCustomerAt = useMemo(() => {
    const customerMessages = messages.filter((message) => message.author === 'customer');
    return customerMessages.length > 0 ? customerMessages[customerMessages.length - 1].at : null;
  }, [messages]);

  const windowOpen = lastCustomerAt ? Date.now() - new Date(lastCustomerAt).getTime() < 24 * 3_600_000 : false;

  if (detail.isError) {
    return <ErrorState message={errorMessage(detail.error)} onRetry={() => void detail.refetch()} />;
  }

  return (
    <div className="conv">
      <section className="pane" aria-label="Konuşma">
        <div className="flex items-center gap-2 border-b border-line px-4 py-2">
          <Link to="/inbox" className="btn btn-ghost btn-icon btn-sm" aria-label="Gelen kutusuna dön" title="Gelen kutusu (Esc)">
            <Icon name="arrowLeft" size={16} />
          </Link>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h1 className="truncate text-[14px]">{contact?.name || (conversation ? formatPhone(conversation.phone) : 'Konuşma')}</h1>
              {conversation?.isTest ? <Badge tone="warn">Test</Badge> : null}
              <Badge tone={conversation?.aiState === 'ai' ? 'signal' : 'neutral'}>
                {conversation?.aiState === 'ai' ? 'AI devrede' : conversation?.aiState === 'human' ? 'Ajan devraldı' : 'AI duraklatıldı'}
              </Badge>
              {conversation?.labels.map((label) => (
                <Badge key={label}>{label}</Badge>
              ))}
            </div>
            <div className="mono text-[11px] text-muted">
              {conversation ? formatPhone(conversation.phone) : ''} · {conversation?.status === 'open' ? 'açık' : conversation?.status === 'pending' ? 'yanıt bekliyor' : 'kapalı'}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {conversation?.aiState === 'ai' ? (
              <button type="button" className="btn btn-sm" onClick={() => aiMutation.mutate('takeover')} disabled={aiMutation.isPending}>
                <Icon name="user" size={14} />
                Devral
              </button>
            ) : (
              <button type="button" className="btn btn-sm" onClick={() => aiMutation.mutate('resume')} disabled={aiMutation.isPending}>
                <Icon name="sparkle" size={14} />
                AI'ya bırak
              </button>
            )}
            {contact ? (
              <Link to={'/kisi/' + contact.id} className="btn btn-sm">
                <Icon name="user" size={14} />
                Kişi 360
              </Link>
            ) : null}
          </div>
        </div>

        {!windowOpen && conversation ? (
          <div className="flex items-center gap-2 border-b border-[#ead9b4] bg-warn-soft px-4 py-1.5 text-[12px] text-warn" role="status">
            <Icon name="clock" size={14} />
            24 saat penceresi kapalı — son müşteri mesajının üzerinden 24 saat geçti. Şablon mesajı gerekir.
          </div>
        ) : null}

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3">
          {detail.isLoading ? (
            <LoadingRows rows={8} columns={2} />
          ) : messages.length === 0 ? (
            <EmptyState icon="ticket" title="Henüz mesaj yok" hint="İlk mesajı yazarak konuşmayı başlatın." />
          ) : (
            <MessageList messages={messages} onRetry={(body) => sendMutation.mutate({ body, note: false })} />
          )}
        </div>

        <div className="border-t border-line bg-surface px-4 py-2">
          <div className="mb-2 flex flex-wrap items-center gap-1">
            {QUICK_REPLIES.map((reply) => (
              <button
                key={reply}
                type="button"
                className="badge hover:bg-surface-2"
                onClick={() => {
                  setDraft((current) => (current ? current + ' ' + reply : reply));
                  composerRef.current?.focus();
                }}
              >
                {reply.length > 32 ? reply.slice(0, 32) + '…' : reply}
              </button>
            ))}
            <label className="ml-auto flex items-center gap-1 text-[12px] text-muted">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 accent-[#b4551b]"
                checked={isNote}
                onChange={(event) => setIsNote(event.target.checked)}
              />
              İç not
            </label>
          </div>

          <div className="flex items-end gap-2">
            <textarea
              ref={composerRef}
              className="textarea min-h-[64px] flex-1"
              placeholder={isNote ? 'İç not yazın (müşteri görmez)…' : 'Mesaj yazın… (Ctrl+Enter ile gönder)'}
              value={draft}
              maxLength={MAX_LEN}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event: KeyboardEvent<HTMLTextAreaElement>) => {
                if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                  event.preventDefault();
                  submit();
                }
              }}
              aria-label={isNote ? 'İç not' : 'Mesaj'}
            />
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              accept="image/*,application/pdf"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                event.target.value = '';
                const mediaMutation = api
                  .uploadMedia(file)
                  .then((media) => {
                    sendMutation.mutate({ body: file.name, note: false, media });
                  })
                  .catch((cause) => {
                    toast.push(errorMessage(cause), 'error');
                  });
                void mediaMutation;
              }}
            />
            <button type="button" className="btn btn-icon" onClick={() => fileRef.current?.click()} aria-label="Dosya ekle" title="Dosya ekle">
              <Icon name="paperclip" size={16} />
            </button>
            <button type="button" className="btn btn-primary" onClick={submit} disabled={!draft.trim() || sendMutation.isPending}>
              <Icon name="send" size={14} />
              Gönder
            </button>
          </div>

          <div className="mt-1 flex items-center justify-between text-[11px] text-muted">
            <span className="flex items-center gap-2">
              <KeyHint keys={['Ctrl', 'Enter']} /> gönder
              <KeyHint keys={['Esc']} /> çık
              <KeyHint keys={['R']} /> yanıtla
            </span>
            <span className="mono">
              {draft.length}/{MAX_LEN}
            </span>
          </div>
        </div>
      </section>

      <aside className="pane conv-rail" aria-label="Kişi özeti">
        {contact ? (
          <div className="flex h-full flex-col overflow-y-auto">
            <div className="border-b border-line px-4 py-3">
              <div className="eyebrow">Kişi</div>
              <div className="mt-1 text-[14px] font-medium">{contact.name || 'Kayıtlı değil'}</div>
              <div className="mono mt-0.5 text-[12px] text-muted">{formatPhone(contact.phone)}</div>
              <div className="mt-2 flex flex-wrap gap-1">
                {contact.optIn ? <Badge tone="ok">Opt-in</Badge> : <Badge tone="danger">Opt-in yok</Badge>}
                {contact.blocked ? <Badge tone="danger">Engelli</Badge> : null}
                {contact.labels.map((label) => (
                  <Badge key={label}>{label}</Badge>
                ))}
              </div>
            </div>

            <div className="border-b border-line px-4 py-3">
              <div className="eyebrow mb-2">Siparişler</div>
              {contact.orders.length === 0 ? (
                <div className="text-[12px] text-muted">Sipariş kaydı yok.</div>
              ) : (
                <ul className="space-y-2">
                  {contact.orders.map((order) => (
                    <li key={order.id} className="text-[12px]">
                      <div className="flex items-center justify-between gap-2">
                        <span className="mono text-[11px] text-muted">{order.id}</span>
                        <span className="badge">{order.status}</span>
                      </div>
                      <div className="mt-0.5 truncate">{order.title}</div>
                      <div className="mono text-[11px] text-muted">
                        {order.amount}
                        {order.tracking ? ' · ' + order.tracking : ''}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="border-b border-line px-4 py-3">
              <div className="eyebrow mb-1">AI durumu</div>
              <div className="flex items-center gap-2 text-[12px]">
                <ToneDot tone={conversation?.aiState === 'ai' ? 'ok' : 'warn'} />
                {conversation?.aiState === 'ai' ? 'AI yanıtlıyor' : 'Ajan devraldı — AI susuyor'}
              </div>
              <div className="mt-1 text-[11px] text-muted">
                Son müşteri mesajı: {lastCustomerAt ? formatDayLabel(lastCustomerAt) + ' ' + formatTime(lastCustomerAt) : '—'}
              </div>
            </div>

            <div className="px-4 py-3">
              <Link to={'/kisi/' + contact.id} className="btn w-full">
                <Icon name="user" size={14} />
                Kişi 360'ı aç
              </Link>
            </div>
          </div>
        ) : (
          <LoadingRows rows={6} columns={2} />
        )}
      </aside>
    </div>
  );
}

function MessageList({ messages, onRetry }: { messages: Message[]; onRetry: (body: string) => void }) {
  let lastDay = '';

  return (
    <div className="space-y-3">
      {messages.map((message) => {
        const day = formatDayLabel(message.at);
        const showDay = day !== lastDay;
        lastDay = day;

        if (message.kind === 'system') {
          return (
            <div key={message.id}>
              {showDay ? <DayDivider label={day} /> : null}
              <div className="flex items-center justify-center gap-2 py-1 text-[11px] text-muted">
                <Icon name="shield" size={12} />
                <span>{message.body}</span>
              </div>
            </div>
          );
        }

        const outbound = message.author === 'agent' || message.author === 'ai';
        const isNote = Boolean(message.isNote);
        const failed = message.status === 'failed' || message.status === 'blocked';

        return (
          <div key={message.id}>
            {showDay ? <DayDivider label={day} /> : null}
            <div className={'flex ' + (isNote ? 'justify-center' : outbound ? 'justify-end' : 'justify-start')}>
              <div className={isNote ? 'w-full max-w-[640px]' : 'max-w-[560px]'}>
                <div className="mb-0.5 flex items-center gap-2 text-[11px] text-muted">
                  <span className="font-medium text-ink-2">
                    {message.author === 'customer' ? 'Müşteri' : message.author === 'ai' ? 'AI' : message.author === 'agent' ? 'Ajan' : 'Sistem'}
                  </span>
                  {isNote ? <Badge tone="warn">İç not</Badge> : null}
                  {message.author === 'ai' && message.aiConfidence !== undefined ? (
                    <span className="mono">güven {message.aiConfidence.toFixed(2)}</span>
                  ) : null}
                  <span className="mono">{formatTime(message.at)}</span>
                  {outbound && message.status ? (
                    <span className={'inline-flex items-center gap-1 ' + (failed ? 'text-danger' : '')}>
                      <Icon name={message.status === 'read' ? 'checkDouble' : message.status === 'failed' || message.status === 'blocked' ? 'warning' : 'check'} size={12} />
                      {statusLabel(message.status)}
                    </span>
                  ) : null}
                </div>

                <div className={'bubble ' + (isNote ? 'bubble-note' : outbound ? 'bubble-out' : '') + (failed ? ' bubble-failed' : '')}>
                  {message.kind === 'image' || message.kind === 'document' ? (
                    <MediaBody message={message} />
                  ) : (
                    message.body
                  )}
                  {failed ? (
                    <div className="bubble-meta flex items-center gap-2 text-danger">
                      <span>{message.blockedReason || 'Gönderilemedi'}</span>
                      <button type="button" className="btn btn-sm" onClick={() => onRetry(message.body)}>
                        <Icon name="refresh" size={12} />
                        Tekrar dene
                      </button>
                    </div>
                  ) : null}
                  {message.aiSources && message.aiSources.length > 0 ? (
                    <div className="bubble-meta">Kaynak: {message.aiSources.join(' · ')}</div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DayDivider({ label }: { label: string }) {
  return (
    <div className="my-3 flex items-center gap-2" role="separator" aria-label={label}>
      <span className="h-px flex-1 bg-line" />
      <span className="text-[11px] text-muted">{label}</span>
      <span className="h-px flex-1 bg-line" />
    </div>
  );
}

/** Mesaj medyası: oturum içi önbellek + sunucu alanları birleşir. */
function MediaBody({ message }: { message: Message }) {
  const cached = mediaFor(message.id);
  // Kalıcı kaynak: GET /api/messages/:id/media (auth + inline). Gönderim anındaki
  // iyimser önizleme için oturum içi önbellek yedek olarak kullanılır.
  const serverUrl = message.hasMedia ? '/api/messages/' + message.id + '/media' : '';
  const url = serverUrl || message.mediaUrl || cached?.url || '';
  const mime = message.mediaMime || cached?.mime || '';
  const name = message.mediaName || cached?.name || message.body;
  const size = message.mediaSize ?? cached?.size ?? 0;

  return (
    <span className="block">
      {url && mime.startsWith('image/') ? (
        <a href={url} target="_blank" rel="noopener noreferrer" className="mb-1 block">
          <img
            src={url}
            alt={name || 'Görsel'}
            className="max-h-[240px] max-w-full rounded-[4px] border border-line object-contain"
            loading="lazy"
          />
        </a>
      ) : null}
      <span className="flex items-center gap-2">
        <Icon name={message.kind === 'image' ? 'image' : 'file'} size={16} className="shrink-0 text-muted" />
        <span className="min-w-0">
          {url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="block truncate font-medium underline underline-offset-2"
              title={mime.startsWith('image/') ? 'Görseli yeni sekmede aç' : 'Dosyayı aç'}
            >
              {name}
            </a>
          ) : (
            <span className="block truncate font-medium">{name}</span>
          )}
          <span className="mono text-[11px] text-muted">
            {size ? formatBytes(size) : 'medya'}
            {mime ? ' · ' + mime : ''}
          </span>
        </span>
      </span>
    </span>
  );
}
